import {
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GlobalSignOutCommand,
  type AuthenticationResultType,
  type InitiateAuthCommandOutput,
  type RespondToAuthChallengeCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';

// Auth is driven directly against the Cognito Identity Provider API (not amazon-cognito-identity-js).
// USER_PASSWORD_AUTH keeps the challenge handling simple; ALLOW_USER_PASSWORD_AUTH is enabled on the
// app client. Tokens are persisted in localStorage and refreshed on demand.

const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
const AWS_REGION = import.meta.env.VITE_AWS_REGION as string;

const client = new CognitoIdentityProviderClient({ region: AWS_REGION });

const TOKENS_KEY = 'heymakr_auth_tokens';
// Refresh the id token this many ms before it actually expires, so callers never sign a request
// with a token that lapses in flight.
const EXPIRY_SKEW_MS = 60_000;

export interface AppSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

// The opaque Cognito challenge session + the username Cognito expects echoed back, threaded between
// the initial sign-in and the follow-up challenge response. `email` is kept for display only.
export interface PendingChallenge {
  session: string;
  username: string;
  email: string;
  destination?: string;
}

export type SignInResult =
  | { type: 'success'; session: AppSession }
  | { type: 'new_password_required'; challenge: PendingChallenge }
  | { type: 'error'; message: string };

function decodeJwt(token: string): Record<string, any> {
  const payload = token.split('.')[1];
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

function persist(result: AuthenticationResultType): AppSession {
  const session: AppSession = {
    idToken: result.IdToken!,
    accessToken: result.AccessToken!,
    // A refresh-token flow does not return a new refresh token; keep the existing one.
    refreshToken: result.RefreshToken ?? loadStored()?.refreshToken ?? '',
    expiresAt: Date.now() + (result.ExpiresIn ?? 3600) * 1000,
  };
  localStorage.setItem(TOKENS_KEY, JSON.stringify(session));
  return session;
}

function loadStored(): AppSession | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as AppSession) : null;
  } catch {
    return null;
  }
}

function clearStored(): void {
  localStorage.removeItem(TOKENS_KEY);
}

// Maps a raw Cognito auth/challenge response to a SignInResult, persisting tokens on completion.
function toSignInResult(
  output: InitiateAuthCommandOutput | RespondToAuthChallengeCommandOutput,
  email: string,
): SignInResult {
  if (output.AuthenticationResult) {
    return { type: 'success', session: persist(output.AuthenticationResult) };
  }

  const session = output.Session ?? '';
  const username = output.ChallengeParameters?.USERNAME ?? email;
  const challenge: PendingChallenge = {
    session,
    username,
    email,
    destination: output.ChallengeParameters?.CODE_DELIVERY_DESTINATION,
  };

  switch (output.ChallengeName) {
    case ChallengeNameType.NEW_PASSWORD_REQUIRED:
      return { type: 'new_password_required', challenge };
    default:
      return { type: 'error', message: `Unsupported sign-in challenge: ${output.ChallengeName}` };
  }
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const username = email.toLowerCase();
  try {
    const output = await client.send(new InitiateAuthCommand({
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: username, PASSWORD: password },
    }));
    return toSignInResult(output, username);
  } catch (err) {
    return { type: 'error', message: err instanceof Error ? err.message : 'Authentication failed' };
  }
}

export async function completeNewPassword(
  challenge: PendingChallenge,
  newPassword: string,
): Promise<SignInResult> {
  try {
    const output = await client.send(new RespondToAuthChallengeCommand({
      ClientId: CLIENT_ID,
      ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
      Session: challenge.session,
      ChallengeResponses: { USERNAME: challenge.username, NEW_PASSWORD: newPassword },
    }));
    return toSignInResult(output, challenge.email);
  } catch (err) {
    return { type: 'error', message: err instanceof Error ? err.message : 'Failed to set new password' };
  }
}

// The pool's password policy (app/infrastructure/lib/constructs/cognito.construct.ts). Kept here so
// the sign-in, new-password and password-reset forms all state the same rule.
export const MIN_PASSWORD_LENGTH = 8;
export const PASSWORD_POLICY_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

// Cognito's exception names mean nothing to an end user; map the ones a password reset can
// realistically hit onto text that says what to do next.
function passwordResetErrorMessage(err: unknown): string {
  switch ((err as { name?: string })?.name) {
    case 'CodeMismatchException':
      return 'That code is not valid. Check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.';
    case 'InvalidPasswordException':
      return `Choose a stronger password. ${PASSWORD_POLICY_HINT}`;
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'NotAuthorizedException':
      // A user still on a temporary password (FORCE_CHANGE_PASSWORD) cannot reset it this way.
      return 'This account cannot be reset yet. If you have not signed in since being invited, use the temporary password from your invitation email.';
    case 'InvalidParameterException':
      return 'This account has no verified email address. Please contact your administrator.';
    default:
      return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
  }
}

/**
 * Step 1 of the reset: ask Cognito to email a confirmation code. The CustomEmailSender trigger
 * delivers it (CustomEmailSender_ForgotPassword).
 *
 * An unknown address is reported as a success on purpose — the screen must not reveal whether an
 * account exists. `destination` is the masked address Cognito sent to, when it tells us.
 */
export async function requestPasswordReset(
  email: string,
): Promise<{ error: string | null; destination?: string }> {
  try {
    const output = await client.send(new ForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email.toLowerCase(),
    }));
    return { error: null, destination: output.CodeDeliveryDetails?.Destination };
  } catch (err) {
    if ((err as { name?: string })?.name === 'UserNotFoundException') return { error: null };
    return { error: passwordResetErrorMessage(err) };
  }
}

// Step 2 of the reset: exchange the emailed code for a new password.
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<{ error: string | null }> {
  try {
    await client.send(new ConfirmForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email.toLowerCase(),
      ConfirmationCode: code.trim(),
      Password: newPassword,
    }));
    return { error: null };
  } catch (err) {
    return { error: passwordResetErrorMessage(err) };
  }
}

// Returns a valid session, transparently refreshing the id/access tokens with the refresh token when
// they are close to expiry. Returns null (and clears storage) when there is no usable session.
export async function getCurrentSession(): Promise<AppSession | null> {
  const stored = loadStored();
  if (!stored) return null;
  if (stored.expiresAt > Date.now() + EXPIRY_SKEW_MS) return stored;
  if (!stored.refreshToken) { clearStored(); return null; }

  try {
    const output = await client.send(new InitiateAuthCommand({
      AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
      ClientId: CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: stored.refreshToken },
    }));
    if (!output.AuthenticationResult) { clearStored(); return null; }
    return persist(output.AuthenticationResult);
  } catch {
    clearStored();
    return null;
  }
}

// Convenience for API/credential callers that only need a fresh id token.
export async function getValidIdToken(): Promise<string | null> {
  return (await getCurrentSession())?.idToken ?? null;
}

export async function signOut(): Promise<void> {
  const stored = loadStored();
  clearStored();
  if (stored?.accessToken) {
    try {
      await client.send(new GlobalSignOutCommand({ AccessToken: stored.accessToken }));
    } catch {
      // Best-effort server-side revocation; local tokens are already cleared.
    }
  }
}

export function getUserIdFromSession(session: AppSession): string {
  return decodeJwt(session.idToken)['sub'] as string;
}

export function getEmailFromSession(session: AppSession): string {
  return decodeJwt(session.idToken)['email'] as string;
}
